// Sui API reference content — mirrors the published article
// "Datanodes Sui API Reference" (nodefleet.net/#/articles/datanodes-sui-api-reference).
// Every example here was captured live against the gateway when the article was
// written (August 2026). `{{KEY}}` is replaced at render time (see src/lib/demoKey.js);
// the demo key itself is intentionally not stored in this repo.
//
// Generated from the article document; keep block shapes in sync with
// src/Components/api/DocBlocks.jsx.

export const SUI_META = {
  "title": "Datanodes Sui API Reference",
  "excerpt": "A practical reference for calling Sui through Datanodes: authenticate once, then query the chain over GraphQL or gRPC. Covers endpoints, available methods, rate limits, and how to migrate off the legacy JSON-RPC.",
  "date": "August 17, 2026",
  "readingTime": "6 min read",
  "tags": [
    "Datanodes",
    "Sui",
    "API Reference",
    "GraphQL",
    "gRPC"
  ],
  "theme": {
    "logo": "/assets/icons/sui.png",
    "accent": "#5AA9E6",
    "accentSoft": "#5AA9E6",
    "surface": "#5AA9E612",
    "bg": "#12111d",
    "border": "#5AA9E630",
    "name": "datanodes-sui-api-reference",
    "coverGradient": "radial-gradient(circle at 30% 20%, #5AA9E633, transparent 60%), linear-gradient(160deg, #12111d 0%, #0A0A14 100%)"
  },
  "author": "Nodefleet Team"
};

export const SUI_BLOCKS = [
  {
    "text": "Everything you need to call Sui through Datanodes — authenticate once, then query the chain over GraphQL or gRPC. This guide covers connecting, the methods available on each interface, copy-paste examples, rate limits, and error handling.",
    "type": "callout"
  },
  {
    "rows": [
      {
        "cells": [
          "rpc.datanodes.nodefleet.net/graphql",
          "sui-grpc.datanodes.nodefleet.net:443",
          "August 2026"
        ]
      }
    ],
    "type": "table",
    "headers": [
      "Base (GraphQL)",
      "Base (gRPC)",
      "Updated"
    ]
  },
  {
    "type": "heading",
    "text": "Connect & authenticate"
  },
  {
    "text": "Sui exposes two query interfaces and you can use either (or both) with the same key. Pick per call:",
    "type": "paragraph"
  },
  {
    "type": "list",
    "items": [
      "GraphQL — shape your own response and follow relationships (objects, balances, epochs) in one request. Best for reads where you want exactly the fields you need.",
      "gRPC — strongly-typed, low-latency access for ledger reads, submitting transactions, Move-package introspection, and real-time checkpoint streaming."
    ]
  },
  {
    "text": "1. Get your API key",
    "type": "subheading"
  },
  {
    "text": "Create a project in the Datanodes dashboard and copy its key (format sk_live_-…). The same key works for GraphQL and gRPC — nothing Sui-specific to enable.",
    "type": "paragraph"
  },
  {
    "text": "Public demo key — try it now. Every example in this guide is runnable as-is with the shared demo key below. It's a free-tier, rate-limited key for evaluation. For production traffic and higher limits, create your own in the dashboard.",
    "type": "callout"
  },
  {
    "type": "code",
    "text": "x-api-key: {{KEY}}"
  },
  {
    "type": "subheading",
    "text": "2. Endpoints & required headers"
  },
  {
    "headers": [
      "GraphQL",
      ""
    ],
    "rows": [
      {
        "cells": [
          "Method",
          "POST"
        ]
      },
      {
        "cells": [
          "URL",
          "https://rpc.datanodes.nodefleet.net/graphql"
        ]
      },
      {
        "cells": [
          "x-api-key",
          "{{KEY}}"
        ]
      },
      {
        "cells": [
          "x-chain",
          "sui-graphql"
        ]
      },
      {
        "cells": [
          "Content-Type",
          "application/json"
        ]
      }
    ],
    "type": "table"
  },
  {
    "headers": [
      "gRPC",
      ""
    ],
    "type": "table",
    "rows": [
      {
        "cells": [
          "Target",
          "sui-grpc.datanodes.nodefleet.net:443"
        ]
      },
      {
        "cells": [
          "Transport",
          "TLS · HTTP/2"
        ]
      },
      {
        "cells": [
          "x-api-key",
          "{{KEY}} (metadata)"
        ]
      },
      {
        "cells": [
          "Chain select",
          "automatic — from the service path"
        ]
      }
    ]
  },
  {
    "text": "GraphQL selects the chain with the x-chain: sui-graphql header. gRPC needs no x-chain — the chain is inferred from the service path (/sui.rpc.*); you only send x-api-key.",
    "type": "paragraph"
  },
  {
    "type": "subheading",
    "text": "3. Network information"
  },
  {
    "headers": [
      "Field",
      "Value"
    ],
    "type": "table",
    "rows": [
      {
        "cells": [
          "Network",
          "Sui mainnet"
        ]
      },
      {
        "cells": [
          "Chain identifier",
          "4btiuiMPvEENsttpZC7CZ53DruC3MAgfznDbASZ7DR6S"
        ]
      },
      {
        "cells": [
          "Native coin / decimals",
          "SUI · 9 (1 SUI = 1e9 MIST)"
        ]
      },
      {
        "cells": [
          "Protocol version",
          "130"
        ]
      },
      {
        "cells": [
          "gRPC packages",
          "sui.rpc.v2, sui.rpc.v2alpha"
        ]
      }
    ]
  },
  {
    "text": "Querying with GraphQL",
    "type": "heading"
  },
  {
    "type": "paragraph",
    "text": "POST a JSON body {\"query\":\"…\"} to the GraphQL URL. Every example below is a live response."
  },
  {
    "type": "subheading",
    "text": "Anatomy of a request — curl"
  },
  {
    "type": "code",
    "text": "curl -s -X POST https://rpc.datanodes.nodefleet.net/graphql \\\n  -H \"x-api-key: {{KEY}}\" \\\n  -H \"x-chain: sui-graphql\" \\\n  -H \"Content-Type: application/json\" \\\n  -d '{\"query\":\"{ chainIdentifier }\"}'\n\n→ { \"data\": { \"chainIdentifier\": \"4btiuiMPvEENsttpZC7CZ53DruC3MAgfznDbASZ7DR6S\" } }"
  },
  {
    "text": "Common queries — current epoch & gas price",
    "type": "subheading"
  },
  {
    "text": "{ epoch { epochId startTimestamp referenceGasPrice } }\n\n→ { \"data\": { \"epoch\": {\n    \"epochId\": 1219,\n    \"startTimestamp\": \"2026-08-14T00:05:36.469Z\",\n    \"referenceGasPrice\": \"100\" } } }",
    "type": "code"
  },
  {
    "type": "subheading",
    "text": "Fetch an object by ID"
  },
  {
    "type": "code",
    "text": "{ object(address: \"0x6\") {\n    version\n    owner { __typename }\n    asMoveObject { contents { type { repr } } }\n} }\n\n→ { \"data\": { \"object\": {\n    \"version\": 917805830,\n    \"owner\": { \"__typename\": \"Shared\" },\n    \"asMoveObject\": { \"contents\": { \"type\": {\n      \"repr\": \"0x2::clock::Clock\" } } } } } }"
  },
  {
    "text": "Protocol config & server limits",
    "type": "subheading"
  },
  {
    "text": "{ protocolConfigs { protocolVersion }\n  serviceConfig { maxQueryDepth maxQueryNodes } }\n\n→ { \"data\": {\n    \"protocolConfigs\": { \"protocolVersion\": 130 },\n    \"serviceConfig\": { \"maxQueryDepth\": 20, \"maxQueryNodes\": 300 } } }",
    "type": "code"
  },
  {
    "type": "callout",
    "text": "Query limits. Requests are capped at depth 20 and 300 nodes. Structure deep traversals as multiple queries if you hit the ceiling."
  },
  {
    "type": "heading",
    "text": "What you can query"
  },
  {
    "text": "Top-level fields on the Sui GraphQL schema. Combine them freely in a single request.",
    "type": "paragraph"
  },
  {
    "headers": [
      "Field",
      "Arguments",
      "Returns"
    ],
    "rows": [
      {
        "cells": [
          "chainIdentifier",
          "—",
          "The chain's genesis-derived identifier."
        ]
      },
      {
        "cells": [
          "epoch",
          "id (optional)",
          "Epoch info — id, timestamps, reference gas price, validator set."
        ]
      },
      {
        "cells": [
          "object",
          "address, version (opt.)",
          "An object's owner, version, digest, and Move contents."
        ]
      },
      {
        "cells": [
          "objects",
          "filter, first/after",
          "A paginated list of objects matching a filter."
        ]
      },
      {
        "cells": [
          "address",
          "address",
          "Account view — balances, owned objects, staked SUI."
        ]
      },
      {
        "cells": [
          "↳ address.balance",
          "coinType",
          "Total balance for a coin type held by the address."
        ]
      },
      {
        "cells": [
          "transactionBlock",
          "digest",
          "A transaction with effects, events, and object changes."
        ]
      },
      {
        "cells": [
          "transactionBlocks",
          "filter, first/after",
          "Filtered, paginated transaction history."
        ]
      },
      {
        "cells": [
          "events",
          "filter, first/after",
          "Emitted Move events matching a filter."
        ]
      },
      {
        "cells": [
          "protocolConfigs",
          "protocolVersion (opt.)",
          "Protocol version and feature/config flags."
        ]
      },
      {
        "cells": [
          "serviceConfig",
          "—",
          "Query limits enforced by the server."
        ]
      }
    ],
    "type": "table"
  },
  {
    "type": "callout",
    "text": "Data window. Live state and recent history resolve normally. A few deep-history / full-scan connections (e.g. the raw checkpoints list and some coin-metadata lookups) return FEATURE_UNAVAILABLE on this endpoint. For anything current — objects, epochs, accounts, recent transactions — GraphQL is fully live."
  },
  {
    "type": "callout",
    "text": "Tip · GraphiQL. Open the GraphQL URL in a browser (with your key set) to explore the full schema interactively with autocomplete and inline docs before you write client code."
  },
  {
    "text": "Calling gRPC",
    "type": "heading"
  },
  {
    "type": "paragraph",
    "text": "Connect over TLS/HTTP-2 to sui-grpc.datanodes.nodefleet.net:443, send your key as the x-api-key metadata on every call. No x-chain needed."
  },
  {
    "text": "TypeScript — @mysten/sui/grpc",
    "type": "subheading"
  },
  {
    "text": "import { SuiGrpcClient } from '@mysten/sui/grpc';\n\nconst client = new SuiGrpcClient({\n  network: 'https://sui-grpc.datanodes.nodefleet.net',\n  headers: { 'x-api-key': '{{KEY}}' },\n});\n\n// read the node/chain info\nconst info = await client.ledgerService.getServiceInfo({});\n\n// fetch an object\nconst obj = await client.ledgerService.getObject({ objectId: '0x6' });",
    "type": "code"
  },
  {
    "type": "subheading",
    "text": "CLI — grpcurl (supply the protos)"
  },
  {
    "type": "code",
    "text": "grpcurl -H \"x-api-key: {{KEY}}\" \\\n  -import-path ./proto -proto sui/rpc/v2/ledger_service.proto \\\n  sui-grpc.datanodes.nodefleet.net:443 \\\n  sui.rpc.v2.LedgerService/GetServiceInfo\n\n→ chainId: \"4btiuiMPvEENsttpZC7CZ53DruC3MAgfznDbASZ7DR6S\"\n  chain:   \"mainnet\"\n  server:  \"sui-node/1.77.2\""
  },
  {
    "type": "callout",
    "text": "Bring your own protos. Server reflection is disabled, so tooling can't auto-discover the schema. Load the sui.rpc.v2 / v2alpha .proto files from the MystenLabs/sui repo (or use the TypeScript SDK, which bundles them)."
  },
  {
    "type": "callout",
    "text": "Method naming. gRPC methods are addressed as /<package>.<Service>/<Method> — e.g. /sui.rpc.v2.LedgerService/GetObject."
  },
  {
    "text": "Available gRPC services & methods",
    "type": "heading"
  },
  {
    "text": "LedgerService (read) — committed ledger data: objects, transactions, checkpoints, epochs",
    "type": "subheading"
  },
  {
    "headers": [
      "Method",
      "Returns"
    ],
    "rows": [
      {
        "cells": [
          "GetServiceInfo",
          "Node identity, chain ID, available checkpoint range."
        ]
      },
      {
        "cells": [
          "GetObject",
          "A single object by ID (optionally at a version)."
        ]
      },
      {
        "cells": [
          "BatchGetObjects",
          "Many objects in one request."
        ]
      },
      {
        "cells": [
          "GetTransaction",
          "A transaction with effects and events, by digest."
        ]
      },
      {
        "cells": [
          "BatchGetTransactions",
          "Many transactions in one request."
        ]
      },
      {
        "cells": [
          "GetCheckpoint",
          "A checkpoint by sequence number or digest."
        ]
      },
      {
        "cells": [
          "GetEpoch",
          "Epoch committee, gas price, and protocol config."
        ]
      }
    ],
    "type": "table"
  },
  {
    "text": "StateService (read) — live-state: ownership, coins, balances",
    "type": "subheading"
  },
  {
    "items": [
      "GetCoinInfo",
      "GetBalance",
      "ListBalances",
      "ListOwnedObjects",
      "ListDynamicFields"
    ],
    "type": "list"
  },
  {
    "text": "MovePackageService (read) — introspect published Move code",
    "type": "subheading"
  },
  {
    "type": "list",
    "items": [
      "GetPackage",
      "GetModule",
      "GetDatatype",
      "GetFunction"
    ]
  },
  {
    "text": "TransactionExecutionService (write) — submit / simulate transactions",
    "type": "subheading"
  },
  {
    "type": "list",
    "items": [
      "ExecuteTransaction",
      "SimulateTransaction"
    ]
  },
  {
    "type": "subheading",
    "text": "Name & Signature — SuiNS resolution, signature checks"
  },
  {
    "items": [
      "Lookup (NameService)",
      "ReverseLookup (NameService)",
      "VerifySignature (SignatureVerification)"
    ],
    "type": "list"
  },
  {
    "type": "subheading",
    "text": "Streaming & proofs"
  },
  {
    "items": [
      "SubscriptionService — SubscribeCheckpoints (paid add-on)",
      "v2alpha.ProofService — GetProof (alpha)"
    ],
    "type": "list"
  },
  {
    "type": "heading",
    "text": "Rate limits & errors"
  },
  {
    "type": "subheading",
    "text": "What your plan includes"
  },
  {
    "headers": [
      "Capability",
      "Free tier",
      "Paid tiers"
    ],
    "type": "table",
    "rows": [
      {
        "cells": [
          "GraphQL queries",
          "Included, rate-limited",
          "Higher RPS & monthly quota"
        ]
      },
      {
        "cells": [
          "gRPC unary calls (reads, execute)",
          "Included, rate-limited",
          "Higher RPS & monthly quota"
        ]
      },
      {
        "cells": [
          "gRPC streaming (SubscribeCheckpoints)",
          "—",
          "Paid add-on, concurrent-stream cap"
        ]
      }
    ]
  },
  {
    "text": "Limits are enforced per organization (per-second RPS and per-month volume). When you exceed them you get a clear error with a ref code (below) — upgrade your plan for more headroom.",
    "type": "paragraph"
  },
  {
    "type": "subheading",
    "text": "gRPC status codes"
  },
  {
    "headers": [
      "Status",
      "Meaning"
    ],
    "rows": [
      {
        "cells": [
          "0 OK",
          "Success."
        ]
      },
      {
        "cells": [
          "16 UNAUTHENTICATED",
          "Missing/invalid x-api-key, or key revoked."
        ]
      },
      {
        "cells": [
          "8 RESOURCE_EXHAUSTED",
          "Rate or monthly limit reached."
        ]
      },
      {
        "cells": [
          "7 PERMISSION_DENIED",
          "Plan doesn't include this capability (e.g. streaming on Free)."
        ]
      },
      {
        "cells": [
          "12 UNIMPLEMENTED",
          "Unknown service/method — check the service path spelling."
        ]
      }
    ],
    "type": "table"
  },
  {
    "text": "Reference codes (in error messages)",
    "type": "subheading"
  },
  {
    "headers": [
      "Ref",
      "Meaning"
    ],
    "rows": [
      {
        "cells": [
          "NF-AU01",
          "API key required — set x-api-key."
        ]
      },
      {
        "cells": [
          "NF-AU02 / AU03",
          "Invalid key format / invalid or revoked key."
        ]
      },
      {
        "cells": [
          "NF-AC03 / AC04",
          "Plan lacks gRPC for this chain / streaming needs a paid plan."
        ]
      },
      {
        "cells": [
          "NF-RL07 / RL08",
          "Per-second rate limit / monthly cap reached."
        ]
      }
    ],
    "type": "table"
  },
  {
    "type": "paragraph",
    "text": "GraphQL errors arrive in the standard errors[] array with an extensions.code (e.g. FEATURE_UNAVAILABLE, GRAPHQL_VALIDATION_FAILED)."
  },
  {
    "text": "Migrating from JSON-RPC",
    "type": "heading"
  },
  {
    "type": "paragraph",
    "text": "Sui's legacy sui_* / suix_* JSON-RPC is being retired. Here's where the common calls go now."
  },
  {
    "rows": [
      {
        "cells": [
          "sui_getObject / sui_multiGetObjects",
          "gRPC LedgerService.GetObject / BatchGetObjects · or GraphQL object"
        ]
      },
      {
        "cells": [
          "sui_getTransactionBlock",
          "gRPC LedgerService.GetTransaction · or GraphQL transactionBlock"
        ]
      },
      {
        "cells": [
          "suix_getBalance",
          "gRPC StateService.GetBalance · or GraphQL address.balance"
        ]
      },
      {
        "cells": [
          "suix_getOwnedObjects",
          "gRPC StateService.ListOwnedObjects · or GraphQL address.objects"
        ]
      },
      {
        "cells": [
          "sui_getCheckpoint / _Latest_",
          "gRPC LedgerService.GetCheckpoint"
        ]
      },
      {
        "cells": [
          "sui_executeTransactionBlock",
          "gRPC TransactionExecutionService.ExecuteTransaction"
        ]
      },
      {
        "cells": [
          "suix_getReferenceGasPrice",
          "GraphQL epoch.referenceGasPrice"
        ]
      },
      {
        "cells": [
          "sui_getNormalizedMoveModule",
          "gRPC MovePackageService.GetModule"
        ]
      }
    ],
    "type": "table",
    "headers": [
      "Legacy JSON-RPC",
      "Now"
    ]
  },
  {
    "text": "Rule of thumb. Reach for gRPC for the lowest-latency single reads, transaction execution, and streaming; reach for GraphQL when you want to shape a response or pull related data — an account's objects, balances, and recent activity — in one round trip.",
    "type": "callout"
  },
  {
    "type": "paragraph",
    "text": "Datanodes · Nodefleet RPC. Get a key in the dashboard and start with a { chainIdentifier } query. Need higher limits, streaming, or help migrating? Contact the team."
  }
];
